import {
  acknowledgeLocalMode,
  abortCloudReturn,
  acknowledgeCloudReturn,
  commitCloudReturn,
  commitLocalMode,
  getLocalRecovery,
  getLocalModeContext,
  pauseLocalRecovery,
  prepareCloudReturn,
  prepareLocalMode,
  readLocalMode,
  recoverLocalModeEvents,
  resolveLocalRecoveryConflict,
  resumeLocalRecovery,
  sendLocalModeCommand,
  startLocalRecovery,
  syncLocalRecovery,
  verifyLocalRecovery,
} from '@/services/localHub/localModeService';
import { getConnectivityState } from '@/services/connectivity/connectivityService';
import { discoverHub } from '@/services/localHub/hubDiscoveryService';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';

import { readTabContext, writeTabContext, clearTabContext } from '@/services/auth/partnerSessionStorage';

const STORAGE_KEY = 'ewu_operational_authority_v1';

export function clearPersistedOperationalAuthority() {
  try {
    clearTabContext(STORAGE_KEY);
  } catch {
    // A full reload will restore cloud authority from the server.
  }
}

function persisted() {
  try { return JSON.parse(readTabContext(STORAGE_KEY) || 'null'); } catch { return null; }
}

function initialState() {
  const cached = OFFLINE_MODE_ENABLED ? persisted() : null;
  return {
    state: cached?.state || 'CLOUD',
    mode: cached?.mode || 'CLOUD_MODE',
    operationEpoch: Number(cached?.operationEpoch || 1),
    session: cached?.session || null,
    transition: null,
    bootstrap: null,
    events: [],
    lastEventSequence: 0,
    cloudReachable: true,
    hubReachable: false,
    connectedDevices: 0,
    printing: { enabled: false, pending: 0, failed: 0, printed: 0 },
    blockedReason: null,
    unsupportedFeatures: [],
    recovery: cached?.recovery || null,
    returnTransition: null,
    commandsFrozen: false,
  };
}

function discovery(rootState) {
  const value = rootState.device.localControlDiscovery;
  if (!value?.url || !value?.profile?.hub_uuid) throw new Error('LOCAL_HUB_UNAVAILABLE');
  return value;
}

async function ensureDiscovery(store) {
  const known = store.state.device?.localControlDiscovery;
  if (known?.url && known?.profile?.hub_uuid) return known;

  const result = await discoverHub(store);
  const resolved = store.state.device?.localControlDiscovery;
  if (resolved?.url && resolved?.profile?.hub_uuid) return resolved;

  const code = result?.last_error?.code || 'LOCAL_HUB_UNAVAILABLE';
  const error = new Error(code);
  error.code = code;
  throw error;
}

function save(state) {
  if (state.mode === 'CLOUD_MODE') {
    clearTabContext(STORAGE_KEY);
    return;
  }
  writeTabContext(STORAGE_KEY, JSON.stringify({
    state: state.state,
    mode: state.mode,
    operationEpoch: state.operationEpoch,
    session: state.session,
    recovery: state.recovery,
  }));
}

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    setContext(state, context) {
      const previousMode = state.mode;
      const previousState = state.state;
      const previousEpoch = state.operationEpoch;
      state.mode = context.operation_mode || 'CLOUD_MODE';
      state.operationEpoch = Number(context.operation_epoch || 1);
      state.session = context.session || null;
      state.connectedDevices = Number(context.connected_devices || 0);
      state.printing = context.printing || state.printing;
      state.unsupportedFeatures = context.unsupported_features || [];
      state.recovery = context.recovery || state.recovery;
      const recoveryStatus = state.recovery?.status || state.session?.status;
      state.transition = state.mode === 'LOCAL_MODE_PREPARING' && state.session
        ? {
          transition_id: state.session.transition_id,
          session_uuid: state.session.session_uuid || state.session.id,
          base_cloud_sequence: state.session.base_cloud_sequence
            ?? state.session.source_sequence
            ?? 0,
        }
        : null;
      state.state = state.mode === 'LOCAL_MODE_PREPARING'
        ? 'PREPARING_LOCAL'
        : state.mode === 'LOCAL_MODE'
        ? (recoveryStatus?.startsWith('RECOVERY')
          || recoveryStatus === 'CLOUD_RETURN_PREPARING'
          ? recoveryStatus : 'LOCAL_ACTIVE')
        : 'CLOUD';
      state.commandsFrozen = recoveryStatus === 'CLOUD_RETURN_PREPARING';
      state.hubReachable = true;
      save(state);
      const authorityChanged = (
        previousMode !== state.mode
        || previousState !== state.state
        || previousEpoch !== state.operationEpoch
      );
      if (
        authorityChanged
        && state.mode !== 'CLOUD_MODE'
        && typeof window !== 'undefined'
      ) {
        window.dispatchEvent(new CustomEvent('ewu-operational-authority-changed', {
          detail: { mode: state.mode, state: state.state },
        }));
      }
    },
    setPreparing(state, message) {
      state.state = 'PREPARING_LOCAL';
      state.transition = message;
      state.session = {
        id: message.session_uuid,
        session_uuid: message.session_uuid,
        state: 'PREPARING',
        status: 'PREPARING',
        source_sequence: message.base_cloud_sequence,
        base_cloud_sequence: message.base_cloud_sequence,
      };
      state.unsupportedFeatures = message.unsupported_features || [];
    },
    setCommitted(state, message) {
      state.state = 'LOCAL_ACTIVE';
      state.mode = 'LOCAL_MODE';
      state.operationEpoch = Number(message.operation_epoch);
      state.session = {
        ...(state.session || {}),
        id: message.session_uuid,
        session_uuid: message.session_uuid,
        state: 'ACTIVE',
        status: 'ACTIVE',
        source_sequence: message.base_cloud_sequence,
        base_cloud_sequence: message.base_cloud_sequence,
      };
      state.transition = null;
      save(state);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ewu-operational-authority-changed', {
          detail: { mode: state.mode, state: state.state },
        }));
      }
    },
    setBootstrap(state, value) { state.bootstrap = value || null; },
    appendEvent(state, event) {
      if (!event || state.events.some((item) => item.event_uuid === event.event_uuid)) return;
      state.events.push(event);
      state.lastEventSequence = Math.max(
        state.lastEventSequence, Number(event.event_sequence || 0),
      );
    },
    setHubUnavailable(state) {
      if (state.mode === 'LOCAL_MODE') {
        state.state = 'LOCAL_HUB_UNAVAILABLE';
        state.blockedReason = 'LOCAL_HUB_UNAVAILABLE';
      }
      state.hubReachable = false;
    },
    setHubAvailable(state) {
      state.hubReachable = true;
      if (state.mode === 'LOCAL_MODE' && state.state === 'LOCAL_HUB_UNAVAILABLE') {
        state.state = 'LOCAL_ACTIVE';
        state.blockedReason = null;
      }
    },
    setCloudReachable(state, value) {
      state.cloudReachable = Boolean(value);
      if (state.mode === 'LOCAL_MODE' && value && state.state === 'LOCAL_ACTIVE') {
        state.state = 'LOCAL_CLOUD_REACHABLE';
      }
    },
    setRecovery(state, value) {
      const isRecoveryEnvelope = Boolean(
        value
        && Object.prototype.hasOwnProperty.call(value, 'recovery'),
      );
      const payload = isRecoveryEnvelope ? (value.recovery || null) : (value || null);
      state.recovery = payload;
      if (payload?.status) state.state = payload.status;
      state.commandsFrozen = payload?.status === 'CLOUD_RETURN_PREPARING';
      save(state);
    },
    setCloudReturnPreparing(state, message) {
      state.commandsFrozen = true;
      state.state = 'CLOUD_RETURN_PREPARING';
      state.returnTransition = message;
      save(state);
    },
    setCloudReturned(state, message) {
      state.mode = 'CLOUD_MODE';
      state.state = 'CLOUD';
      state.operationEpoch = Number(message.operation_epoch);
      state.session = null;
      state.recovery = null;
      state.returnTransition = null;
      state.commandsFrozen = false;
      state.bootstrap = null;
      state.events = [];
      state.lastEventSequence = 0;
      state.blockedReason = null;
      save(state);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ewu-operational-authority-changed', {
          detail: {
            mode: 'CLOUD_MODE',
            state: 'CLOUD',
            operationEpoch: state.operationEpoch,
          },
        }));
      }
    },
    resetForPartnerSession(state) {
      clearPersistedOperationalAuthority();
      Object.assign(state, initialState());
    },
    setCloudAuthority(state, payload = {}) {
      state.state = 'CLOUD';
      state.mode = 'CLOUD_MODE';
      state.operationEpoch = Number(payload.operation_epoch || state.operationEpoch || 1);
      state.session = null;
      state.transition = null;
      state.bootstrap = null;
      state.recovery = null;
      state.returnTransition = null;
      state.commandsFrozen = false;
      state.blockedReason = null;
      state.cloudReachable = true;
      state.hubReachable = Boolean(payload.hub_reachable);
      save(state);
    },
  },
  actions: {
    async initialize({ commit, rootState }) {
      if (!OFFLINE_MODE_ENABLED) return { operation_mode: 'CLOUD_MODE' };
      const controlDiscovery = await ensureDiscovery(this);
      const context = await getLocalModeContext(this, controlDiscovery);
      const cloudConfirmed = getConnectivityState().backendAvailable === true;
      const cloudMode = rootState.device?.restaurantOperationMode;
      if (
        cloudConfirmed
        && cloudMode === 'CLOUD_MODE'
        && context.operation_mode !== 'CLOUD_MODE'
      ) {
        const cloudContext = {
          ...context,
          operation_mode: 'CLOUD_MODE',
          operation_epoch: Number(
            rootState.device?.operationEpoch || context.operation_epoch || 1,
          ),
          session: null,
          recovery: null,
          stale_hub_operation_mode: context.operation_mode,
        };
        commit('setCloudAuthority', {
          operation_epoch: cloudContext.operation_epoch,
          hub_reachable: true,
        });
        return cloudContext;
      }
      commit('setContext', context);
      if (context.operation_mode === 'LOCAL_MODE') {
        await this.dispatch('operationalMode/loadBootstrap');
        await this.dispatch('operationalMode/loadRecovery').catch(() => {});
      }
      return context;
    },
    async prepare({ commit, rootState }) {
      const message = await prepareLocalMode(this, discovery(rootState));
      commit('setPreparing', message);
      return message;
    },
    async acknowledge({ rootState }, { message, readiness = {} }) {
      return acknowledgeLocalMode(this, discovery(rootState), message, readiness);
    },
    async commit({ commit, dispatch, rootState, state }) {
      if (!state.transition?.transition_id) throw new Error('LOCAL_TRANSITION_REQUIRED');
      const message = await commitLocalMode(
        this, discovery(rootState), state.transition.transition_id,
      );
      commit('setCommitted', message);
      await dispatch('loadBootstrap');
      return message;
    },
    async loadBootstrap({ commit, state }) {
      const sessionUuid = state.session?.session_uuid || state.session?.id;
      if (!sessionUuid) throw new Error('LOCAL_MODE_SESSION_NOT_ACTIVE');
      const controlDiscovery = await ensureDiscovery(this);
      const response = await readLocalMode(
        this, controlDiscovery, sessionUuid, 'pos/bootstrap',
      );
      // Local Hub read endpoints return a signed `{ meta, data }` envelope;
      // consumers of the store work with the bootstrap payload itself.
      const bootstrap = response?.data?.data && response?.data?.meta
        ? response.data.data
        : response?.data || {};
      commit('setBootstrap', bootstrap);
      return bootstrap;
    },
    async command({ commit, dispatch, rootState, state }, command) {
      if (state.mode !== 'LOCAL_MODE') throw new Error('LOCAL_MODE_SESSION_NOT_ACTIVE');
      if (state.commandsFrozen) throw new Error('RECOVERY_FINALIZATION_IN_PROGRESS');
      const response = await sendLocalModeCommand(this, discovery(rootState), {
        ...command,
        session_uuid: state.session.session_uuid || state.session.id,
        operation_epoch: state.operationEpoch,
        client_created_at: new Date().toISOString(),
      });
      if (response.event) commit('appendEvent', response.event);
      await dispatch('loadBootstrap');
      return response;
    },
    async recoverEvents({ commit, rootState, state }) {
      const sessionUuid = state.session?.session_uuid || state.session?.id;
      const response = await recoverLocalModeEvents(
        this, discovery(rootState), sessionUuid, state.lastEventSequence,
      );
      response.events.forEach((event) => commit('appendEvent', event));
      return response;
    },
    async loadRecovery({ commit, rootState }) {
      const response = await getLocalRecovery(this, discovery(rootState));
      commit('setRecovery', response);
      return response;
    },
    async startRecovery({ commit, rootState }) {
      const response = await startLocalRecovery(this, discovery(rootState));
      commit('setRecovery', response);
      return response;
    },
    async pauseRecovery({ commit, rootState }) {
      const response = await pauseLocalRecovery(this, discovery(rootState));
      commit('setRecovery', response);
      return response;
    },
    async resumeRecovery({ commit, rootState }) {
      const response = await resumeLocalRecovery(this, discovery(rootState));
      commit('setRecovery', response);
      return response;
    },
    async syncRecovery({ commit, rootState }) {
      const response = await syncLocalRecovery(this, discovery(rootState));
      commit('setRecovery', response);
      return response;
    },
    async verifyRecovery({ commit, rootState }) {
      const response = await verifyLocalRecovery(this, discovery(rootState));
      commit('setRecovery', response.recovery);
      return response;
    },
    async prepareReturn({ commit, rootState }) {
      const message = await prepareCloudReturn(this, discovery(rootState));
      commit('setCloudReturnPreparing', message);
      return message;
    },
    async acknowledgeReturn({ rootState, state }, message) {
      return acknowledgeCloudReturn(this, discovery(rootState), message, {
        lastLocalEventSequence: state.lastEventSequence,
        ...(message.readiness || {}),
      });
    },
    async abortReturn({ commit, rootState }) {
      const response = await abortCloudReturn(this, discovery(rootState));
      commit('setRecovery', response);
      return response;
    },
    async commitReturn({ commit, rootState }) {
      const message = await commitCloudReturn(this, discovery(rootState));
      commit('setCloudReturned', message);
      return message;
    },
    async resolveConflict({ commit, rootState }, { conflictUuid, action }) {
      const response = await resolveLocalRecoveryConflict(
        this, discovery(rootState), conflictUuid, action,
      );
      commit('setRecovery', response);
      return response;
    },
  },
};
